import { Component, computed, effect, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPlus, faPenToSquare, faTrash, faLink, faGavel, faRightToBracket } from '@fortawesome/free-solid-svg-icons';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData, Chart, registerables } from 'chart.js';
import { AuthService } from '../../services/auth';
import { LeagueService, League, LeagueRosters, LeagueStats } from '../../services/league';
import { LeagueFormModal } from '../../components/league-form-modal/league-form-modal';
import { LeagueActions } from '../../components/league-actions/league-actions';

Chart.register(...registerables);

const POSITION_COLORS: Record<string, string> = {
  QB: '#5e548e',
  RB: '#0077b6',
  WR: '#06a77d',
  TE: '#d4a373',
  K: '#9d0208',
  DEF: '#6c757d',
};

function getPositionColor(position: string): string {
  return POSITION_COLORS[position] || '#999999';
}

function sortedPositions(positionKeys: Set<string>): string[] {
  const order = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];
  return [...positionKeys].sort((a, b) => {
    const ai = order.indexOf(a);
    const bi = order.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

@Component({
  selector: 'app-dashboard',
  imports: [CommonModule, DatePipe, RouterLink, FontAwesomeModule, LeagueFormModal, LeagueActions, BaseChartDirective],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css',
})
export class Dashboard implements OnInit, OnDestroy {
  user = computed(() => this.authService.currentUser());

  readonly currentYear = new Date().getFullYear();

  faPlus = faPlus;
  faPenToSquare = faPenToSquare;
  faTrash = faTrash;
  faLink = faLink;
  faGavel = faGavel;
  faRightToBracket = faRightToBracket;

  readonly featuredGlyphs = [0, 1, 2, 3, 4, 5, 6, 7];
  readonly cardGlyphs = [0, 1, 2, 3];

  readonly ACCENT_COLORS = [
    'oklch(62% 0.12 145)',
    'oklch(66% 0.13 40)',
    'oklch(58% 0.13 250)',
    'oklch(60% 0.13 195)',
    'oklch(62% 0.12 300)',
    'oklch(65% 0.13 60)',
  ];

  leagues = signal<League[]>([]);
  isLoading = signal(false);
  error = signal<string | null>(null);
  showForm = signal(false);
  isEditing = signal(false);
  editingAsCommissioner = signal(false);
  currentLeague: League = {
    name: '',
    max_teams: 12,
    team_budget: 200,
    roster_size: 16,
    team_name: '',
  };

  rosterData = signal<LeagueRosters | null>(null);
  rosterLoading = signal(false);
  statsData = signal<LeagueStats | null>(null);
  statsLoading = signal(false);

  now = signal(new Date());
  private timer: ReturnType<typeof setInterval> | null = null;

  copiedLeagueId = signal<number | null>(null);
  selectedLeagueId = signal<number | null>(null);

  featuredLeague = computed(() => {
    const ls = this.leagues();
    if (!ls.length) return null;
    const selected = this.selectedLeagueId();
    if (selected !== null) return ls.find(l => l.id === selected) ?? null;
    const now = this.now();
    const upcoming = ls
      .filter(l => l.draft_datetime && new Date(l.draft_datetime) > now)
      .sort((a, b) => new Date(a.draft_datetime!).getTime() - new Date(b.draft_datetime!).getTime());
    return upcoming[0] ?? ls[0];
  });

  draftCountdown = computed(() => {
    const fl = this.featuredLeague();
    if (!fl?.draft_datetime) return '';
    const diff = new Date(fl.draft_datetime).getTime() - this.now().getTime();
    if (diff <= 0) return '';
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
    return `${h}h ${m}m`;
  });

  constructor(
    private authService: AuthService,
    private leagueService: LeagueService
  ) {
    effect(() => {
      const fl = this.featuredLeague();
      if (fl?.id) this.loadFeaturedData(fl);
    });
  }

  async ngOnInit(): Promise<void> {
    this.timer = setInterval(() => this.now.set(new Date()), 30000);
    await this.loadLeagues();
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  async loadLeagues(): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const data = await this.leagueService.getAllLeagues();
      this.leagues.set(data);
      this.selectedLeagueId.set(null);
    } catch (err) {
      this.error.set('Failed to load leagues');
      console.error('Error loading leagues:', err);
    } finally {
      this.isLoading.set(false);
    }
  }

  showCreateForm(): void {
    this.showForm.set(true);
    this.isEditing.set(false);
    this.editingAsCommissioner.set(true);
    this.currentLeague = {
      name: '',
      max_teams: 12,
      team_budget: 200,
      roster_size: 16,
      team_name: '',
      draft_datetime: null,
    };
  }

  showEditForm(league: League): void {
    this.showForm.set(true);
    this.isEditing.set(true);
    this.editingAsCommissioner.set(league.is_commissioner ?? false);
    this.currentLeague = {
      ...league,
      draft_datetime: this.toDatetimeLocalString(league.draft_datetime),
    };
  }

  private toDatetimeLocalString(value: string | null | undefined): string {
    if (!value) return '';
    const d = new Date(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  cancelForm(): void {
    this.showForm.set(false);
    this.isEditing.set(false);
    this.editingAsCommissioner.set(false);
    this.error.set(null);
  }

  async saveLeague(league: League): Promise<void> {
    this.error.set(null);
    try {
      if (this.isEditing() && league.id) {
        if (this.editingAsCommissioner()) {
          await this.leagueService.updateLeague(league.id, league);
        } else {
          await this.leagueService.updateTeamName(league.id, league.team_name || '');
        }
      } else {
        await this.leagueService.createLeague(league);
      }
      await this.loadLeagues();
      this.cancelForm();
    } catch (err) {
      this.error.set(`Failed to ${this.isEditing() ? 'update' : 'create'} league`);
      console.error('Error saving league:', err);
    }
  }

  async deleteLeague(league: League): Promise<void> {
    if (!league.id) return;
    if (!confirm(`Are you sure you want to delete "${league.name}"? This will also delete all associated teams and members.`)) return;
    this.error.set(null);
    try {
      await this.leagueService.deleteLeague(league.id);
      await this.loadLeagues();
    } catch (err) {
      this.error.set('Failed to delete league');
      console.error('Error deleting league:', err);
    }
  }

  selectLeague(league: League): void {
    this.selectedLeagueId.set(league.id ?? null);
  }

  private loadFeaturedData(league: League): void {
    if (!league.id) return;

    this.rosterLoading.set(true);
    this.rosterData.set(null);
    this.leagueService.getLeagueRosters(league.id)
      .then(data => this.rosterData.set(data))
      .catch(err => console.error('Error fetching rosters:', err))
      .finally(() => this.rosterLoading.set(false));

    this.statsLoading.set(true);
    this.statsData.set(null);
    this.leagueService.getLeagueStats(league.id)
      .then(data => this.statsData.set(data))
      .catch(err => console.error('Error fetching stats:', err))
      .finally(() => this.statsLoading.set(false));
  }

  costBreakdownChartData = computed<ChartData<'bar'>>(() => {
    const data = this.statsData();
    if (!data) return { labels: [], datasets: [] };
    const allPositions = new Set<string>();
    for (const team of data.teams) {
      for (const pos of Object.keys(team.position_spending)) allPositions.add(pos);
    }
    const sorted = sortedPositions(allPositions);
    return {
      labels: data.teams.map(t => t.name),
      datasets: sorted.map(pos => ({
        label: pos,
        data: data.teams.map(t => t.position_spending[pos] || 0),
        backgroundColor: getPositionColor(pos),
      })),
    };
  });

  costBreakdownChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: $${ctx.parsed.y}` } },
    },
    scales: {
      x: { stacked: true },
      y: { stacked: true, title: { display: true, text: 'Amount Spent ($)' } },
    },
  };

  positionChartData = computed<ChartData<'bar'>>(() => {
    const data = this.statsData();
    if (!data) return { labels: [], datasets: [] };
    const allPositions = new Set<string>();
    for (const team of data.teams) {
      for (const pos of Object.keys(team.position_counts)) allPositions.add(pos);
    }
    const sorted = sortedPositions(allPositions);
    return {
      labels: data.teams.map(t => t.name),
      datasets: sorted.map(pos => ({
        label: pos,
        data: data.teams.map(t => t.position_counts[pos] || 0),
        backgroundColor: getPositionColor(pos),
      })),
    };
  });

  positionChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' } },
    scales: {
      x: { stacked: true },
      y: { stacked: true, title: { display: true, text: 'Players' }, ticks: { stepSize: 1 } },
    },
  };

  costChartData = computed<ChartData<'bar'>>(() => {
    const data = this.statsData();
    if (!data || data.draft_history.length === 0) return { labels: [], datasets: [] };
    const maxCost = Math.max(...data.draft_history.map(d => d.cost));
    const binSize = maxCost <= 10 ? 1 : maxCost <= 50 ? 5 : 10;
    const numBins = Math.ceil(maxCost / binSize) + 1;
    const bins = new Array(numBins).fill(0);
    for (const pick of data.draft_history) bins[Math.floor(pick.cost / binSize)]++;
    const labels = bins.map((_, i) => {
      const low = i * binSize;
      return binSize === 1 ? `$${low}` : `$${low}-${low + binSize - 1}`;
    });
    return {
      labels,
      datasets: [{ label: 'Players', data: bins, backgroundColor: 'rgba(0,119,182,0.8)', borderColor: '#0077b6', borderWidth: 1 }],
    };
  });

  costChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { title: { display: true, text: 'Draft Cost' } },
      y: { title: { display: true, text: 'Number of Players' }, ticks: { stepSize: 1 } },
    },
  };

  hasDraftData = computed(() => {
    const data = this.statsData();
    return data !== null && data.draft_history.length > 0;
  });

  getJoinUrl(league: League): string {
    return `${window.location.origin}/join/${league.hash}`;
  }

  async copyJoinLink(league: League): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.getJoinUrl(league));
      this.copiedLeagueId.set(league.id ?? null);
      setTimeout(() => this.copiedLeagueId.set(null), 1500);
    } catch (err) {
      console.error('Failed to copy join link:', err);
    }
  }

  getLeagueStatus(league: League): { text: string; tone: string } {
    const now = this.now();
    const full = (league.team_count ?? 0) >= league.max_teams;
    if (!full) {
      return { text: `LOBBY · ${league.team_count ?? 0}/${league.max_teams}`, tone: 'oklch(60% 0.13 195)' };
    }
    if (!league.draft_datetime) {
      return { text: 'PENDING', tone: 'oklch(55% 0.06 80)' };
    }
    const draft = new Date(league.draft_datetime);
    if (draft > now) {
      const hours = (draft.getTime() - now.getTime()) / 3600000;
      if (hours < 24) return { text: 'DRAFT TONIGHT', tone: 'oklch(58% 0.18 25)' };
      return { text: 'DRAFT UPCOMING', tone: 'oklch(58% 0.13 250)' };
    }
    return { text: 'POST-DRAFT', tone: 'oklch(55% 0.09 250)' };
  }

  getLeagueAccent(idx: number): string {
    return this.ACCENT_COLORS[idx % this.ACCENT_COLORS.length];
  }

  formatDraftDate(dt: string | null | undefined): string {
    if (!dt) return '—';
    const d = new Date(dt);
    const now = this.now();
    const isToday = d.toDateString() === now.toDateString();
    const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const h = d.getHours() % 12 || 12;
    const min = d.getMinutes() ? `:${String(d.getMinutes()).padStart(2, '0')}` : '';
    const ampm = d.getHours() >= 12 ? 'P' : 'A';
    return isToday ? `TODAY ${h}${min}${ampm}` : `${days[d.getDay()]} ${h}${min}${ampm}`;
  }

  getFirstName(name: string | undefined): string {
    return name?.split(' ')[0] ?? 'there';
  }

  getFeaturedChecklist(league: League): [string, boolean][] {
    return [
      ['Roster slots configured', league.roster_size > 0],
      [`Budget set ($${league.team_budget})`, league.team_budget > 0],
      ['Draft time set', !!league.draft_datetime],
      ['All teams joined', (league.team_count ?? 0) >= league.max_teams],
    ];
  }

  isDraftLive(league: League): boolean {
    return !!league.draft_datetime && new Date(league.draft_datetime).getTime() < this.now().getTime();
  }

  getChecklistCount(league: League): number {
    return this.getFeaturedChecklist(league).filter(([, done]) => done).length;
  }

  getInitials(name: string | null | undefined): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
}
